import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { getFullHtmlCode, getMjmlHtmlCode } from '@webapp/account/src/components/share/email/lib';
import { createThunkRequest, POST } from '@webapp/account/src/libs/request';

import { DATETIME_SERVER_FORMAT, UI_DATETIME_FORMAT } from '@webapp/common/lib/date';

import { patchObject } from '../lib';
import { initialLicense, mapLicense, mapQuotas } from '../profile';
import { selectCurrentSurveyId, selectMailing } from '../survey/selectors';

import type { MailFilters } from './filters';
import type { Mail } from './mailing';
import { EmailDetailsMode, initialMail, MailEditMode, MailType, RecipientType } from './mailing';
import { mapEmailDetail, mapGroup, mapMail, mapRecipient, mapStat, reverseMapMail } from './map';
import type { Recipient } from './recipient';
import { selectCurrentMail, selectCurrentMailId, selectRecipientType } from './selectors';

export const getMailList = createThunkRequest(
    'mailing/getMailList',
    async ({ surveyId }: { surveyId: number }) => {
        const { rows } = await POST('show/email_list', { surveyId });

        return {
            items: rows.map(mapMail),
        };
    },
    { notify: false },
);

export const getCommonEmailStat = createThunkRequest(
    'mailing/getCommonEmailStat',
    async ({ startDatetime, status, surveyId }: { surveyId: number; startDatetime: string; status: number }) => {
        const { dates, stat } = await POST('get/list_stat', {
            survey: surveyId,
            status,
            senddate: startDatetime,
        });

        return {
            stat: mapStat(stat[0]),
            sendDates: dates,
        };
    },
    { notify: false },
);

export const emailStatDownloadCSV = createThunkRequest(
    'mailing/emailStatDownloadCSV',
    async ({ id, filters }: { id: number; filters: MailFilters }) => {
        const { file } = await POST('get/csv_stat', {
            id,
            status: filters.status,
        });

        return { file };
    },
    { notify: false },
);

export const getEmailDetails = createThunkRequest(
    'mailing/getEmailDetails',
    async (
        {
            surveyId,
            filters,
            limit,
            page,
            mode,
        }: {
            surveyId: number;
            filters: MailFilters;
            limit: number;
            page: number;
            mode: EmailDetailsMode;
        },
        { getState },
    ) => {
        const state = getState();
        const recipientType = selectRecipientType(state);
        const mailingId = selectCurrentMailId(state);
        const reminder = Number(recipientType === RecipientType.NO_REPLY);
        const args: AnyObject = {
            survey: surveyId,
            status: filters.status,
            senddate: filters.startDatetime,
            reminder,
            limit,
            page,
        };
        if (mode === EmailDetailsMode.VIEW) {
            args.list_id = mailingId;
        }
        if (mode === EmailDetailsMode.CREATE) {
            args.group_recipients = 1;
        }

        const { count, dates, stat } = await POST('get/list_recipient', args as any);

        return {
            emailDetails: stat.map(mapEmailDetail),
            count,
            page,
            filters,
            sendDates: dates,
        };
    },
    { notify: false },
);

export const getMailGroups = createThunkRequest(
    'mailing/getMailGroups',
    async () => {
        const { rows } = await POST('show/email_groups', {
            excludeHidden: 1,
        });

        return {
            mailGroups: rows.map(mapGroup),
        };
    },
    { notify: false },
);

export const sendCurrentMail = createThunkRequest(
    'mailing/sendCurrentMail',

    async (_, { getState }) => {
        const state = getState();
        const { id, sendDatetime, sendNow } = selectCurrentMail(state);
        const surveyId = selectCurrentSurveyId(state);
        const datetime = sendNow ? undefined : sendDatetime;

        const req: any = {
            list: id,
        };

        if (datetime && !sendNow) {
            req.rundate = datetime.format(DATETIME_SERVER_FORMAT);
        }

        await POST('run/email_list', req);

        const message = sendNow
            ? 'Рассылка будет готова для отправки в течении 1 минуты'
            : `Рассылка будет отправлена ${dayjs(datetime).format(UI_DATETIME_FORMAT)}`;

        return {
            message,
            surveyId,
        };
    },
);

export const removeMailGroup = createThunkRequest(
    'mailing/removeMailGroup',
    async ({ listId, groups }: { listId: number; groups: Array<number> }) => {
        await POST('delete/group_from_mail', {
            list: listId,
            groups,
        });

        return {
            groups,
        };
    },
);

export const addExistingMailGroup = createThunkRequest(
    'mailing/addExistingMailGroup',
    async ({ groupId, linkParams }: { groupId: number; linkParams?: { [key: string]: string } }, { getState }) => {
        const list = selectCurrentMailId(getState());
        const { message } = await POST('add/group_to_list', {
            list,
            group: groupId,
            linkParams,
        });

        return {
            message,
            groupId,
        };
    },
);

export const updateMailingGroupEmails = createThunkRequest(
    'mailing/updateMailingGroupEmails',
    async ({ groupId, emails }: { groupId: number; emails: Array<string> }) => {
        const { message } = await POST('change/email_group', {
            id: groupId,
            data: JSON.stringify(emails),
        });

        return {
            message,
            groupId,
            emails,
        };
    },
);

export const removeMailGroupFromMailing = createThunkRequest(
    'mailing/removeMailGroupFromMailing',
    async ({ listId, groups }: { listId: number; groups: Array<number> }) => {
        const { message } = await POST('delete/group_from_list', {
            list: listId,
            groups,
        });

        return {
            message,
            groups,
        };
    },
);

export const getMailQuotasGetByDate = createThunkRequest(
    'mailing/getMailQuotasGetByDate',
    async ({ date, sendNow }: { date: string; sendNow: boolean }) => {
        const { license, surveys } = await POST('show/quotas', sendNow ? {} : { date });

        return {
            license: {
                ...cloneDeep(initialLicense),
                ...mapLicense(license),
                surveys: mapQuotas(surveys),
            },
        };
    },
    { notify: false },
);

export const loadMailing = createThunkRequest(
    'mailing/loadMailing',
    async ({ mailId, surveyId }: { mailId: number; surveyId: number }) => {
        const { groups, mail, recipient_count, recipients, tags, duplicate } = await POST('show/mail_list', {
            surveyId,
            mailId,
        });
        const item = mapMail(mail);

        item.recipients = recipients.map(mapRecipient);
        item.mailGroups = groups.map(mapGroup);
        item.stat = {
            all: recipient_count.all,
            answered: recipient_count.answered,
            notAnswered: recipient_count.not_answered,
        };
        item.tags = tags;
        item.duplicate = duplicate;

        return {
            item,
        };
    },
    { notify: false },
);

export const addRecipient = createThunkRequest(
    'mailing/addRecipient',
    async ({ mailId, email }: { mailId: number; email: string }) => {
        const { id, message } = await POST('add/email_recipient', {
            list: mailId,
            recipient: email,
        });

        return {
            email,
            message,
            id,
        };
    },
);

export const changeMailGroupName = createThunkRequest(
    'mailing/changeMailGroupName',
    async ({ groupId, name }: { groupId: number; name: string }) => {
        const { message } = await POST('change/email_group', {
            id: groupId,
            name,
        });
        return {
            message,
            groupId,
            name,
        };
    },
);

export const deleteMailGroup = createThunkRequest(
    'mailing/deleteMailGroup',
    async ({ groups }: { groups: Array<number> }) => {
        const { message } = await POST('delete/email_group', {
            groups,
        });

        return {
            message,
            groups,
        };
    },
);

export const addRecipientsFromCSV = createThunkRequest(
    'mailing/addRecipientsFromCSV',
    async ({ file, listId }: { file: File; listId: number }) => {
        let { group } = await POST('upload/csv/file', {
            upfile: file,
            list: listId,
        });

        group = mapGroup(group);

        return {
            group,
        };
    },
);

export const removeRecipient = createThunkRequest(
    'mailing/removeRecipient',
    async ({ recipients, listId }: { recipients: Array<Recipient>; listId: number | string }) => {
        const ids = recipients.map(({ id }) => id);
        const { message } = await POST('delete/email_recipient', {
            id: JSON.stringify(ids),
            listId,
        });

        return {
            message,
            ids,
        };
    },
);

export const sendTestEmail = createThunkRequest(
    'mailing/sendTestEmail',
    async ({ mailId, email }: { mailId: number; email: string }) => {
        await POST('send/test', {
            list: mailId,
            email,
        });

        return {
            message: `Тестовое письмо успешно отправлено на адрес ${email}`,
        };
    },
);

export const changeMail = createThunkRequest(
    'mailing/changeMail',
    async ({ change, id }: { id: number; change?: Partial<Mail> }) => {
        const updatedParams = reverseMapMail(change);

        const { message } = await POST('change/email_list', {
            id,
            ...updatedParams,
        });

        return {
            id,
            message,
            change,
        };
    },
);

export const addMail = createThunkRequest(
    'mailing/addMail',
    async ({ surveyId, mailType, title }: { surveyId: number; mailType: MailType; title: string }, { getState }) => {
        const mailing = selectMailing(getState());
        const mail = cloneDeep(initialMail);

        const req: AnyObject = {
            surveyId,
            reminder: mailType === MailType.NOTIFY ? 1 : 0,
            identification: mail.bindMail ? 1 : 0,
            who: mailType === MailType.NOTIFY ? RecipientType.NO_REPLY : null,
        };

        if (title) {
            req.title = title;
        }

        if (mailing) {
            req.params = JSON.stringify(mailing);
            req.template = mailing.editMode === MailEditMode.HTML ? mailing.fullHtml : getMjmlHtmlCode(mailing);
        } else {
            req.params = JSON.stringify(mail.params);
            req.template = getMjmlHtmlCode(mail.params);
        }

        const { mail: item, message } = await POST('add/email_list', req as any);

        return {
            surveyId,
            mail: mapMail(item),
            title: req.title,
            message,
        };
    },
);

export const addMailGroup = createThunkRequest(
    'mailing/addMailGroup',
    async (
        {
            emails,
            hidden,
            name,
        }: {
            name: string;
            emails: Array<string>;
            hidden: boolean;
        },
        { getState },
    ) => {
        const currentId = selectCurrentMailId(getState());
        const { id, message } = await POST('add/mail_group', {
            name,
            data: JSON.stringify(emails),
            hidden: Number(hidden),
            list: currentId,
        });

        return {
            newGroupId: id,
            message,
            emails,
            name,
            hidden,
        };
    },
);

export const changeMailParam = createThunkRequest(
    'mailing/changeMailParam',
    async (
        { mailId, param, value }: { mailId: number; param: string; value?: string | number | boolean },
        { getState },
    ) => {
        const mail: Mail = selectCurrentMail(getState());
        const params = cloneDeep(mail.params);

        patchObject(params, param, value);

        const req: JsonValue = {
            id: mailId,
            params: JSON.stringify(params),
        };

        if (param === 'editMode' && value === MailEditMode.HTML && !params.fullHtml) {
            params.fullHtml = mail.params.body ? mail.params.body : getMjmlHtmlCode(params, false);
        }

        const template =
            params.editMode === MailEditMode.TEMPLATE
                ? getMjmlHtmlCode(params, false)
                : getFullHtmlCode(params.fullHtml, false);

        if (template !== mail.template) {
            req.template = template;
        }

        const { message, duplicate } = await POST('change/email_list', req);

        return { message, params, duplicate };
    },
);
